import assertNever from "assert-never";
import * as React from "react";
import * as messages from "../messages";
import { AgencyId, VisitBroadcastId } from "../messages";
import { ChatRoomId } from "../messages/ObjectIds";
import { STRINGS } from "../strings";
import { AppAgenciesState } from "../system/AppState/AppAgenciesState";
import { Visit } from "../system/AppState/AppVisitsState";
import { LatestChatSummary } from "../system/AppState/ChatState";
import * as time from "../time";
import { translate } from "../translate";
import { NavigationClickContext } from "../ui/NavigationClickContext";
import { pulseCSSClass } from "../ui/pulseCssClass";
import { fmap } from "../utils/fmap";
import { List } from "../utils/List";
import { withDefault } from "../utils/withDefault";

export interface ChatsListProps {
  chats: List<LatestChatSummary>;
  agencies: AppAgenciesState;
  visits: List<Visit>;
  onChatRoomClick: (chatRoomId: ChatRoomId) => void;
}

/**
 * A list of the Chat Rooms, each one showing the latest message and number of new messages
 */
export class ChatsList extends React.Component<ChatsListProps> {
  constructor(props: ChatsListProps) {
    super(props);
  }

  // TODO Shouldn't be here!!!! Should be in toplevel page
  delayedClickContext = new NavigationClickContext();

  handleChatRoomClick = (chatRoomId: ChatRoomId, _clickHandled: () => void) => {
    // clickHandled();
    // this.delayedClickContext.click(() => {
    this.props.onChatRoomClick(chatRoomId);
    // });
  };

  render(): JSX.Element {
    return (
      <div className="ChatsList__root">
        <div className="ChatsList__chatsCont">
          {this.props.chats.map((chat: LatestChatSummary) => (
            <ChatLatestMessageRow
              key={ChatRoomId.unwrap(chat.chatRoomId)}
              chat={chat}
              agencies={this.props.agencies}
              visits={this.props.visits}
              onChatRoomClick={this.handleChatRoomClick}
            />
          ))}
        </div>
      </div>
    );
  }
}

interface ChatLatestMessageProps {
  chat: LatestChatSummary;
  agencies: AppAgenciesState;
  visits: List<Visit>;
  onChatRoomClick: (chatRoomId: ChatRoomId, clickHandled: () => void) => void;
}

const ChatLatestMessageRow = React.memo((props: ChatLatestMessageProps) => {
  const rootRef = React.useRef<HTMLElement | null>(null);

  const rowClickAnimation = () => {
    if (rootRef.current !== null) {
      pulseCSSClass(rootRef.current, "ChatsList__chatRow_clickAnim");
    }
  };

  const handleRootRef = (e: HTMLElement | null) => {
    rootRef.current = e;

    if (rootRef.current !== null) {
      rootRef.current.addEventListener("touchstart", () => {
        pulseCSSClass(rootRef.current as any, "ChatsList__chatRow_clickAnim");
      });
    }
  };

  return (
    <div
      className="ChatsList__chatRow"
      onClick={() => props.onChatRoomClick(props.chat.chatRoomId, rowClickAnimation)}
      ref={handleRootRef}
    >
      <div className="ChatsList__chatRow_iconCont">
        {renderChatIcon(props.agencies, props.chat.chatRoomType)}
      </div>
      <div className="ChatsList__chatRow_contentCont">
        <div className="ChatsList__chatRow_content_headerRow">
          <div className="ChatsList__chatRow_content_headerText">
            {chatTitle(props.agencies, props.visits, props.chat.chatRoomType)}
          </div>
          <div className="ChatsList__chatRow_timestamp">
            {time.formatTimestamp(props.chat.latestChatMessage.createdAt)}
          </div>
        </div>
        <div className="ChatsList__chatRow_content_bodyRow">
          <div className="ChatsList__chatRow_content_headerBodyText">
            {renderChatPayload(props.chat.latestChatMessage.payload)}
          </div>
          {props.chat.numNewMessages === 0 ? (
            ""
          ) : (
            <div className="ChatsList__chatRow_numNewMessages">{props.chat.numNewMessages}</div>
          )}
        </div>
      </div>
    </div>
  );
});

function renderChatIcon(
  agencies: AppAgenciesState,
  chatRoomType: messages.ChatRoomType
): JSX.Element {
  let chatIcon = "./assets/default_profile.png";

  switch (chatRoomType.type) {
    case "CaregiverVisitChat":
      return <div className="ChatsList__visitIcon" />;
    case "AgencyCaregiverChat":
    case "AgencyMemberCaregiverChat":
    case "CaregiverPatientChat": {
      const agency = agencies.agencies.get(chatRoomType.agencyId);

      if (agency !== undefined) {
        if (agency.agency.logoUrl !== null) {
          chatIcon = agency.agency.logoUrl;
        }
      }

      return <img className="ChatsList__agencyIcon" src={chatIcon} />;
    }
    default:
      return assertNever(chatRoomType);
  }
}

function chatTitle(
  agencies: AppAgenciesState,
  visits: List<Visit>,
  chatRoomType: messages.ChatRoomType
) {
  switch (chatRoomType.type) {
    case "CaregiverVisitChat": {
      const agencyId = lookupVisitAgency(visits, chatRoomType.visitId);
      if (agencyId === null) {
        return (
          <>
            <strong>{translate(STRINGS.LOADING)}</strong>
            <span>{translate(STRINGS.VISIT_CHAT)}</span>
          </>
        );
      }
      const agency = agencies.agencies.get(agencyId);
      if (agency !== undefined) {
        return (
          <>
            <strong>{agency.agency.name}</strong>
            <span>{translate(STRINGS.VISIT_CHAT)}</span>
          </>
        );
      }
      return "Unknown Agency: Visit Chat";
    }
    case "AgencyCaregiverChat": {
      const agency = agencies.agencies.get(chatRoomType.agencyId);
      if (agency !== undefined) {
        return (
          <>
            <strong>{agency.agency.name}</strong>
            <span>{chatRoomType.department}</span>
          </>
        );
      }

      return (
        <>
          <strong>Unknown Agency</strong>
          <span>{chatRoomType.department}</span>
        </>
      );
    }
    case "AgencyMemberCaregiverChat": {
      const agency = agencies.agencies.get(chatRoomType.agencyId);
      const agencyMember = agencies.allAgencyMembers.get(chatRoomType.agencyMemberId);

      if (agency !== undefined && agencyMember !== undefined) {
        let agencyMemberHeader =
          agencyMember.details.firstName + " " + agencyMember.details.lastName;

        if (agencyMember.details.status !== "Active") {
          agencyMemberHeader = agencyMemberHeader.concat(` (${agencyMember.details.status})`);
        }

        return (
          <>
            <strong>{agencyMemberHeader}</strong>
            <span>{agency.agency.name}</span>
          </>
        );
      }
      return "Unknown Agency Member";
    }
    case "CaregiverPatientChat": {
      const agencyName = withDefault(
        fmap(agencies.agencies.get(chatRoomType.agencyId), (x) => x.agency.name),
        translate(STRINGS.LOADING)
      );

      return (
        <>
          <strong>{chatRoomType.patientName}</strong>
          <span>{agencyName}</span>
        </>
      );
    }
    default:
      return assertNever(chatRoomType);
  }
}

function renderChatPayload(payload: messages.ChatPayload): JSX.Element {
  switch (payload.type) {
    case "Message":
      return <>{payload.message}</>;
    case "Image":
      throw new Error("TODO");
    default:
      return assertNever(payload);
  }
}

function lookupVisitAgency(visits: List<Visit>, visitId: VisitBroadcastId): AgencyId | null {
  for (const visit of visits) {
    if (visit.publicVisitBroadcastDetails.id === visitId) {
      return visit.publicVisitBroadcastDetails.agency.id;
    }
  }

  return null;
}
